// decimal-input.component.ts
import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-dim-decimal-input',
  templateUrl: './dim-decimal-input.component.html',
  styleUrls: ['./dim-decimal-input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DimDecimalInputComponent),
      multi: true,
    }
  ]
})
export class DimDecimalInputComponent implements ControlValueAccessor, OnInit {
  @Input() label: string = '';
  @Input() disabled: boolean = false;
  @Input() placeholder: string = '';
  @Input() id: string = 'form_decimal_input';
  @Input() maxDecimals = 6;
  @Input() visibleDecimals = 2;

  value = 0;
  showFull = false;
  storageKey = 'dim-decimal-input-config';

  private _displayValue: string = '';
  private isEditing: boolean = false;

  ngOnInit() {
    this.loadConfig();
    this.updateDisplayValue();
  }

  get displayValue(): string {
    return this._displayValue;
  }

  set displayValue(value: string) {
    this._displayValue = value;
  }

  get tooltipText(): string {
    return this.showFull ? 'Mostrar menos decimais' : 'Mostrar mais decimais';
  }

  updateDisplayValue() {
    if (!this.isEditing) {
      this.displayValue = this.formatNumber(this.value);
    }
  }

  formatNumber(value: number): string {
    if (value === null || value === undefined) return '';

    const fixed = this.showFull ? this.maxDecimals : this.visibleDecimals;
    const parts = value.toFixed(fixed).split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const decimalPart = parts.length > 1 ? ',' + parts[1] : '';

    return integerPart + decimalPart;
  }

  onInputChange(event) {
    this.isEditing = true;

    const cursorPos = event.target.selectionStart;
    let inputValue = event.target.value;

    const cleanValue = inputValue.replace(/[^0-9.,]/g, '');

    if (cleanValue !== inputValue) {
      inputValue = cleanValue;
      event.target.value = cleanValue;
    }

    let normalizedValue = inputValue.replace(/\./g, '').replace(',', '.');

    const decimalParts = normalizedValue.split('.');
    if (decimalParts.length > 1 && decimalParts[1].length > this.maxDecimals) {
      decimalParts[1] = decimalParts[1].substring(0, this.maxDecimals);
      normalizedValue = decimalParts.join('.');

      const displayParts = inputValue.split(',');
      if (displayParts.length > 1) {
        displayParts[1] = decimalParts[1];
        inputValue = displayParts.join(',');
        event.target.value = inputValue;
      }
    }

    if (!normalizedValue || isNaN(parseFloat(normalizedValue))) {
      this.value = 0;
      this.displayValue = inputValue;
      this.onChange(this.value);

      setTimeout(() => {
        event.target.setSelectionRange(cursorPos, cursorPos);
      });
      return;
    }

    this.value = parseFloat(parseFloat(normalizedValue).toFixed(this.maxDecimals));
    this.displayValue = inputValue;
    this.onChange(this.value);

    setTimeout(() => {
      event.target.setSelectionRange(cursorPos, cursorPos);
    });
  }

  onInputBlur() {
    this.isEditing = false;
    this.updateDisplayValue();
    this.onTouched();
  }

  onInputFocus(event) {
    event.target.select();
  }

  toggleDecimals() {
    this.showFull = !this.showFull;
    this.updateDisplayValue();
    this.saveConfig();
  }

  loadConfig() {
    try {
      const config = localStorage.getItem(this.storageKey);
      if (config) {
        const parsedConfig = JSON.parse(config);
        this.showFull = parsedConfig.showFull || false;
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
      this.showFull = false;
    }
  }

  saveConfig() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({ showFull: this.showFull }));
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
    }
  }

  onChange = (_: any) => { };
  onTouched = () => { };

  writeValue(val: any): void {
    this.value = val ?? 0;
    this.updateDisplayValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
